/**
 * billable_events — Phase 12 billing pipeline abstraction (DEC-013, master spec §88).
 *
 * **CRITICAL Phase 12 constraint:** the billing engine MUST write to and read from
 * this table. It MUST NOT query `haul_captures` directly for billing.
 *
 * Phase 2 ships the table shape + RLS + indexes. Phase 12 (Billing) builds the
 * pipeline that populates this table from haul completions, asset deployments,
 * rental day-counters, and manual billing events; and the invoice generator that
 * reads from this table grouped by (tenant_id, billed_at, invoice_id).
 *
 * Why the abstraction: hauling-only billing was the legacy paelode model.
 * lodeX Pro's billing engine bills hauls + asset rentals + asset deployments +
 * manual events through a single, uniform event stream — the billing engine
 * stays simple by reading one table; haul_captures stays a domain-pure record
 * of field data.
 *
 * Three durable surfaces carry this constraint forward:
 *   (a) THIS docstring — read by anyone touching the schema file
 *   (b) ROADMAP.md Phase 12 section — read by the future Phase 12 planner
 *   (c) 02-CONTEXT.md <deferred> — read by the future Phase 12 planner during context-load
 *
 * Tenant FK target: §88's SQL says `hauler_tenants(tenant_id)`; the planner
 * standardizes on the unified `tenants` table per CONTEXT D-04 (same convention
 * as DEC-011 yards/trailers and the former DEC-012 paelode_tenant_config, dropped by 0203).
 *
 * Sources:
 *   - master spec §88 (BILLABLE_EVENTS ABSTRACTION TABLE — user-authored)
 *   - gsd/DECISIONS_LOG.md DEC-013 (user-authored)
 *   - .planning/phases/02-database-foundation/02-CONTEXT.md D-21
 *   - ROADMAP.md Phase 12 implementation constraint line
 *
 * Locked contract (future phases must NOT change without DEC-NNN):
 *   - 4-value event_type CHECK allowlist (HAUL_COMPLETION/ASSET_DEPLOYMENT/ASSET_RENTAL_DAY/MANUAL)
 *   - 7-value billing_method CHECK allowlist (PER_BBL/HOURLY/FLAT_RATE/MILEAGE/PER_DAY_RENTAL/PER_DEPLOYMENT/PER_UNIT)
 *   - billable_amount is GENERATED ALWAYS AS (quantity * COALESCE(rate, 0)) STORED — never written by app code
 *   - tenant-scoped RLS via app.current_tenant_id session setting
 *   - 5 indexes per §88: (tenant_id), (job_id), (invoice_id), (billed_at), (source_id, source_type)
 *
 * generatedAlwaysAs note (CONTEXT D-21 / Path A): drizzle-orm@^0.45.2's
 * `generatedAlwaysAs(as: SQL | T['data'] | (() => SQL))` takes a single
 * argument and emits `GENERATED ALWAYS AS (...) STORED` by default — the
 * STORED mode is implicit (no second `{ mode: 'stored' }` argument needed
 * in this version; that signature was 0.40-era). The generated migration
 * should emit the STORED column inline; no Plan 02-05 ALTER TABLE epilogue
 * is required (Path B fallback documented in the plan was NOT needed).
 */

import {
  check,
  date,
  index,
  jsonb,
  numeric,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';

/**
 * BILLABLE_EVENT_TYPES — exactly 4 values per master spec §88 / DEC-013.
 *
 * Stored as text + CHECK at the column level. Adding a 5th value requires
 * DEC-NNN (and a Phase 12 implementation update — the billing pipeline
 * needs a handler per event_type).
 */
export const BILLABLE_EVENT_TYPES = [
  'HAUL_COMPLETION',
  'ASSET_DEPLOYMENT',
  'ASSET_RENTAL_DAY',
  'MANUAL',
] as const;
export type BillableEventType = (typeof BILLABLE_EVENT_TYPES)[number];

/**
 * BILLING_METHODS — exactly 7 values per master spec §88 / DEC-013.
 *
 * The billing-method dimension cuts ACROSS event_type — e.g. ASSET_DEPLOYMENT
 * may bill PER_DEPLOYMENT or PER_UNIT depending on the asset configuration.
 * Phase 12's invoice generator reads this column to choose its rate-× -quantity
 * formula.
 */
export const BILLING_METHODS = [
  'PER_BBL',
  'HOURLY',
  'FLAT_RATE',
  'MILEAGE',
  'PER_DAY_RENTAL',
  'PER_DEPLOYMENT',
  'PER_UNIT',
] as const;
export type BillingMethod = (typeof BILLING_METHODS)[number];

/**
 * billable_events — DEC-013 / master spec §88 abstraction table.
 *
 * Tenant-scoped, RLS-enforced. 5 indexes per §88: tenant_id, job_id,
 * invoice_id, billed_at, (source_id, source_type). 2 typed CHECK constraints
 * (event_type allowlist, billing_method allowlist).
 *
 * billable_amount is GENERATED ALWAYS AS (quantity * COALESCE(rate, 0)) STORED —
 * Postgres computes it on every INSERT/UPDATE; app code MUST NOT write to it. Drizzle
 * 0.45.x's `generatedAlwaysAs(sql)` emits the STORED clause by default.
 *
 * Phase 12 implementation responsibility (NOT Phase 2):
 *   - Pipeline INSERTs rows from haul completions, asset deployments,
 *     rental day-counters, manual events.
 *   - Invoice generator GROUPs by (tenant_id, billed_at, invoice_id) and
 *     emits invoices.
 *   - The pipeline reads from haul_captures (etc.) but writes invoice line
 *     items here. Phase 12 MUST NOT query haul_captures directly for billing.
 */
export const billableEvents = pgTable(
  'billable_events',
  {
    eventId: uuid('event_id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    // CHECK constraint defined below in the (t) => [...] callback.
    eventType: text('event_type').notNull(),
    // FK to originating record (haul_capture, asset_deployment, etc.) — see
    // master spec §88 for the type-by-event_type mapping. No `.references()`
    // because source tables vary by event_type (polymorphic-style).
    sourceId: uuid('source_id').notNull(),
    // 'haul_capture' | 'asset_deployment' | 'manual' (stored alongside source_id
    // so consumers can resolve the FK target type).
    sourceType: text('source_type').notNull(),
    // CHECK constraint defined below in the (t) => [...] callback.
    billingMethod: text('billing_method').notNull(),
    // BBL hauled, hours worked, days deployed, units, miles — varies by billing_method.
    quantity: numeric('quantity').notNull(),
    // Rate at time of billing event (snapshot — never changes after event creation).
    // NULLABLE (Phase 12.1, migration 0076): rate NULL is the "needs pricing" signal
    // for an unpriced LINE_ITEM product (billable_amount computes to $0 via COALESCE).
    // A genuine $0-rate product is rate 0 (distinct from NULL). Non-line-item writers
    // always supply a concrete rate.
    rate: numeric('rate'),
    // GENERATED ALWAYS AS (quantity * COALESCE(rate, 0)) STORED — drizzle 0.45.x
    // default mode. COALESCE so an unpriced (rate NULL) row yields amount 0, not NULL.
    billableAmount: numeric('billable_amount').generatedAlwaysAs(sql`quantity * COALESCE(rate, 0)`),
    currency: text('currency').notNull().default('USD'),
    // Nullable — non-job events (e.g. asset rental days) have no job.
    jobId: uuid('job_id').references(() => jobs.id, { onDelete: 'set null' }),
    // No FK per D-21 — operators may be external entities (not in operators table).
    // LEGACY identity: only jobs from the old model populate it (jobs.operator_id).
    operatorId: uuid('operator_id'),
    // DEC-192 option A (migration 0083) — the CURRENT customer identity, from
    // jobs.hauler_operator_id. A separate column rather than reusing operator_id,
    // because this is what invoices group by and show a customer: one column holding
    // two kinds of id (with no FK to distinguish them) is the silent slip DEC-192
    // warned about. Readers prefer this and fall back to operator_id for legacy rows.
    // No FK, same reasoning as operator_id above.
    haulerOperatorId: uuid('hauler_operator_id'),
    // Set when included in an invoice (Phase 12 invoice generator).
    invoiceId: uuid('invoice_id'),
    // Set when invoice is generated (NULL = unbilled).
    billedAt: timestamp('billed_at', { withTimezone: true }),
    // For rental events: start of billing period.
    periodStart: timestamp('period_start', { withTimezone: true }),
    // For rental events: end of billing period.
    periodEnd: timestamp('period_end', { withTimezone: true }),
    notes: text('notes'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
    // DEC-356 / migration 0179 — billing edited this unbilled row (#50). original_quantity
    // is the quantity before the FIRST edit (NULL = never edited); adjusted_at/_by are the
    // last edit. Display only: the generator, invoice and export never read them.
    originalQuantity: numeric('original_quantity'),
    adjustedAt: timestamp('adjusted_at', { withTimezone: true }),
    adjustedBy: uuid('adjusted_by').references(() => users.id, { onDelete: 'set null' }),
    // Migration 0250 (gap 6) — the ticket's service date: the company-local day it is billed
    // on (hauler_tenant_settings.bill_date_basis), set at approve on every event the ticket
    // emits and copied to lines billing adds. NULL on rows from before 0250 (read as the
    // created_at day). The invoice PERIOD still selects by created_at (billingPeriodColumn).
    serviceDate: date('service_date'),
    // gap 1 (0271): provenance {v:1,...}; review/billing screens only (rate-source-readers-guard)
    rateSource: jsonb('rate_source'),
  },
  (t) => [
    // 5 indexes per CONTEXT D-21 / master spec §88.
    index('billable_events_tenant_idx').on(t.tenantId),
    index('billable_events_job_idx').on(t.jobId),
    index('billable_events_invoice_idx').on(t.invoiceId),
    index('billable_events_billed_at_idx').on(t.billedAt),
    index('billable_events_source_idx').on(t.sourceId, t.sourceType),
    // Migration 0083 — invoice generation selects unbilled events by customer identity.
    index('billable_events_hauler_operator_idx').on(t.haulerOperatorId),
    // Phase 12 / Task 12.0 — PARTIAL unique index scoped to the haul-billing writer's
    // source_types, so a retried InReview approve cannot double-emit (double-bill).
    // Migration 0053. Non-haul event types (asset/manual) are intentionally unconstrained.
    // Phase 12.1 — widened to add 'haul_capture_line_item' (LINEITEM-04). Without
    // this, a retried InReview approve double-emits (double-bills) every line
    // item — ON CONFLICT DO NOTHING only suppresses duplicates for source_types
    // the index predicate covers (Postgres cannot infer a partial index for a
    // source_type it doesn't list).
    // DEC-191 / migration 0081 — widened again for 'job_hours', the source_type used
    // when an HOURLY job has billable hours but no capture to key them on.
    // DEC-356 / migration 0179 — 'billing_service_charge' (an extra billing adds; the
    // source_id is the client's UUID, so a retried add can't bill twice) and #52 /
    // DEC-358's 'haul_capture_disposal_fee', widened together because 0179 lands first.
    uniqueIndex('billable_events_source_unique')
      .on(t.sourceId, t.sourceType)
      .where(
        sql`source_type IN ('haul_capture', 'haul_capture_hours', 'haul_capture_service_charge', 'haul_capture_line_item', 'job_hours', 'billing_service_charge', 'haul_capture_disposal_fee')`,
      ),
    // 4-value event_type allowlist.
    check(
      'billable_events_event_type_check',
      sql`event_type IN ('HAUL_COMPLETION', 'ASSET_DEPLOYMENT', 'ASSET_RENTAL_DAY', 'MANUAL')`,
    ),
    // 7-value billing_method allowlist.
    check(
      'billable_events_billing_method_check',
      sql`billing_method IN ('PER_BBL', 'HOURLY', 'FLAT_RATE', 'MILEAGE', 'PER_DAY_RENTAL', 'PER_DEPLOYMENT', 'PER_UNIT')`,
    ),
    // RLS — tenant-scoped per D-21.
    pgPolicy('billable_events_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type BillableEvent = typeof billableEvents.$inferSelect;
export type NewBillableEvent = typeof billableEvents.$inferInsert;
export const billableEventSelectSchema = createSelectSchema(billableEvents);
export const billableEventInsertSchema = createInsertSchema(billableEvents);
